import AddIcon from '@mui/icons-material/Add';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, IconButton, styled, Switch, Stack, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo, useState, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IClientSettings } from 'wisestep-api';
import {
  WsButton,
  getDateUsingFormat,
  WsTypography,
  WsTypoVariants,
  EmptyTeamsAndUsersIcon,
  Item,
  WsCard,
  CardVariant,
  WsColors,
  WsTypoColors,
  SearchBarNew,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import WsContextMenuHandler from '../../../components/WsContextMenu/WsContextMenuHandler';
import Loader from '../../../features/conversationWidget/components/Loader';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import OrgUnit from '../../../images/Avatar/OrgUnit.svg';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { StyledDataGrid } from '../../teams/components/TeamListNew';
import WsAvatar from '../../teams/components/WsAvatar';

import { StyledPaginationBox } from './OrganizationalUnitLayoutNew';

export interface ILabelWithStatus {
  label: string;
  labelTwo?: string;
  status: boolean;
}

interface ITableState {
  businessUnitName: string;
  index: number;
  memberCount: number | undefined;
  createdOn: string | undefined;
  owner: string | undefined;
  id: number | string;
}

export const HeaderBox = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  minWidth: '100%',
}));

export const StyledSwitch = styled(Switch)(() => ({
  top: '0.5vw',
}));

interface IClientSettingsLayout {
  title: string;
  headerList: string[];
  businessUnits?: IClientSettings[];
  itemsList?: ILabelWithStatus[];
  dataCallback?(data: boolean, index: number): void;
  handleAddButton?: () => void;
  addButtonText?: string;
  backUrl: string;
  loader?: boolean;
  bussinesCount?: Map<string, number>;
  getBusinessUnit?: (index: number) => void;
}

const BusinessUnitLayoutNew = ({
  title,
  businessUnits,
  bussinesCount,
  addButtonText,
  handleAddButton,
  loader,
  getBusinessUnit,
}: IClientSettingsLayout) => {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [tableData, setTableData] = useState<ITableState[]>([]);
  const methods = useForm();
  const { setValue, handleSubmit } = methods;

  const getData = useCallback(() => {
    return (
      businessUnits?.map((item, index) => ({
        businessUnitName: item.name || 'Not Available',
        index: index,
        memberCount: bussinesCount?.[item.id] ?? 0,
        createdOn: item.createdOn
          ? getDateUsingFormat(item.createdOn.toString(), 'DD MMM YYYY')
          : '',
        owner: item.createdBy ?? '',
        id: item.id ?? index,
      })) || []
    );
  }, [businessUnits, bussinesCount]);

  useMemo(() => {
    if (businessUnits) {
      const updatedTableData = getData();
      setTableData(updatedTableData);
    }
  }, [businessUnits, getData]);

  const onEdit = (index: number) => {
    getBusinessUnit && getBusinessUnit(index);
  };

  const onDrillDown = (index: number, isOpenNewTab = false) => {
    const url = '/business_unit/user-view';
    const state = {
      businessUnitDescription: businessUnits?.[index].description,
      businessUnitName: businessUnits?.[index].name,
      businessUnitId: businessUnits?.[index].id,
      isSetting: title !== 'Teams Business Units',
    };

    isOpenNewTab
      ? openUrlInNewTab(`${url}?state=${getEncodedQueryParams(state)}`)
      : navigate(url, {
          state: state,
        });
  };

  const index = useRef(0);
  const defaultContextMenuValues = { showContext: false, x: 0, y: 0 };
  const [contextMenuDetails, setContextMenuDetails] = useState(defaultContextMenuValues);
  const menuItems = [
    {
      name: 'Open in new tab',
      onclick: () => {
        onDrillDown(index.current, true);
      },
    },
  ];
  const handleContextMenu = (e, indexValue) => {
    index.current = indexValue;
    e.preventDefault();
    setContextMenuDetails({
      showContext: true,
      x: e.pageX,
      y: e.pageY,
    });
  };

  const enableAddback = title != 'Teams Business Units' && addButtonText && !isEmpty(businessUnits);
  const columns: GridColDef[] = [
    {
      field: 'businessUnitName',
      headerName: 'Business Unit Name',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={2} alignItems="center">
          <WsAvatar src={OrgUnit} size="small" variant="rounded" />
          <WsTypography
            color={WsTypoColors.PRIMARY750}
            cursor="pointer"
            variant={WsTypoVariants.SUB_TITLE}
            onClick={() => onDrillDown(params.row.index)}
            onContextMenu={(e) => handleContextMenu(e, params.row.index)}
          >
            {params.value}
          </WsTypography>
        </Stack>
      ),
    },
    {
      field: 'memberCount',
      headerName: 'Users',
      flex: 1,
      renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
    },
    {
      field: 'createdOn',
      headerName: 'Date Created',
      sortable: false,
      flex: 1,
      renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      flex: 1,
      renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
    },
  ];

  if (title === 'Business Units') {
    columns.push({
      field: 'id',
      headerName: '',
      width: 60,
      renderCell: (params) => (
        <Grid item>
          <IconButton onClick={() => onEdit(params.row.index)}>
            <BorderColorIcon />
          </IconButton>
        </Grid>
      ),
    });
  }

  const resetTofetch = (returnFlag) => {
    const data = getData();
    setTableData(data);
    if (returnFlag) return data;
  };

  const onSearch = (data) => {
    const originalData = resetTofetch(true);
    if (data.query) {
      const filteredData = originalData?.filter((item) =>
        item.businessUnitName.toLowerCase().includes(data.query?.toLowerCase())
      );
      filteredData && setTableData(filteredData);
      setPageNo(0);
    } else {
      clear();
    }
  };

  const clear = () => {
    setValue('query', '');
    resetTofetch(false);
  };

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {tableData.length}
        </WsTypography>
      </Box>
      {tableData.length > 10 && (
        <WsDataGridPagination
          count={Math.ceil(tableData.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <>
      {title != 'Teams Business Units' && addButtonText && !isEmpty(businessUnits) && (
        <WsBreadCrumbs
          breadCrumbsList={getSettingBreadCrumbList(
            ISettingBreadCrumbTypes.TEAMS_AND_USERS,
            Pages['BUSINESS_UNITS']
          )}
        />
      )}
      <Grid
        container
        item
        xs={12}
        bgcolor={WsColors.White}
        sx={{ boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)', borderRadius: 2, p: 2 }}
        overflow="hidden"
      >
        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          sx={{ padding: '5px 20px 10px 15px' }}
        >
          <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
            {title}
          </WsTypography>
          <Tooltip title="View units details here">
            <IconButton sx={{ p: 0, ml: 1 }}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        {contextMenuDetails.showContext && (
          <WsContextMenuHandler
            top={contextMenuDetails.y}
            left={contextMenuDetails.x}
            menuItems={menuItems}
            setContextMenuDetails={setContextMenuDetails}
          />
        )}
        {businessUnits && businessUnits.length > 0 && !loader ? (
          <>
            <Grid item display="flex" alignItems="center" justifyContent="space-between" xs={12}>
              {!enableAddback && (
                <FormProvider {...methods}>
                  <Box p={2} width="42%" component="form" onSubmit={handleSubmit(onSearch)}>
                    <SearchBarNew size="small" name="query" type="submit" onClear={clear} />
                  </Box>
                </FormProvider>
              )}
              {title != 'Teams Business Units' && addButtonText && !isEmpty(businessUnits) && (
                <Box ml="auto" mr={0}>
                  <WsButton startIcon={<AddIcon />} variant="contained" onClick={handleAddButton}>
                    <>Add {addButtonText}</>
                  </WsButton>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              {paginationComponent}
              <StyledDataGrid
                autoHeight
                hideFooter
                columns={columns}
                rows={tableData ?? []}
                page={pageNo}
                pageSize={pageSize}
              />
              {tableData.length > 10 && paginationComponent}
            </Grid>
          </>
        ) : loader ? (
          <Box textAlign="center" pt={15} px={4}>
            <Loader />
          </Box>
        ) : title === 'Teams Business Units' ? (
          <WsCard
            sx={{ mt: 4, width: '100%', boxShadow: 'none' }}
            isButton={false}
            cardType={CardVariant.WITH_LIGHT_BG}
            middleComponent={
              <Item>
                <Stack alignItems="center" spacing={3}>
                  <EmptyTeamsAndUsersIcon />
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>Its lonely here</WsTypography>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    No Business units created in the workspace
                  </WsTypography>
                </Stack>
              </Item>
            }
          />
        ) : (
          <NoDataFoundCard
            text="No Business Unit created"
            subElement={
              <Box p={2}>
                <WsButton
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={handleAddButton}
                >{`Add ${addButtonText}`}</WsButton>
              </Box>
            }
          />
        )}
      </Grid>
    </>
  );
};

export default BusinessUnitLayoutNew;
